import React, { useEffect, useState } from 'react';
import FunctionCombobox from './FunctionCombobox';
import ScriptValueField from './ScriptValueField';
import { vmixAPI } from '../../lib/api';

// Functions whose Value is a script NAME, so the Value box becomes a picker.
// The Dynamic variants take script CODE, not a name, and are deliberately out.
const SCRIPT_NAME_FUNCTIONS = new Set(['scriptstart', 'scriptstop']);

const isScriptFunction = (fn) => SCRIPT_NAME_FUNCTIONS.has((fn || '').trim().toLowerCase());

// Editor for a single vMix cue-action step. The controls cascade left to right:
//
//   Instance → Function → Input → Value
//
// Choosing the instance first means the Input dropdown can show that specific
// vMix's inputs (fetched on demand), so you pick "Camera 2" on the vMix you're
// actually targeting. Primary uses the show's already-loaded input list.
export default function VmixStepForm({ draft, setDraft, vmixInputs, vmixInstances, showId }) {
  const instances = vmixInstances || [];
  const targetId = draft.target_instance_id || '';
  const inputCls = 'bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-blue-500';
  const scriptFn = isScriptFunction(draft.function);

  // Inputs for non-Primary instances, fetched lazily and cached by instance id.
  // Primary ('') always uses the vmixInputs prop (loaded when the show connects).
  const [inputsByInstance, setInputsByInstance] = useState({});
  const [loadingInputs, setLoadingInputs] = useState(false);

  useEffect(() => {
    if (!targetId) return;                       // Primary → use vmixInputs prop
    if (scriptFn) return;                        // no Input control to fill
    if (inputsByInstance[targetId]) return;      // already fetched
    let cancelled = false;
    setLoadingInputs(true);
    vmixAPI.inputs(showId, targetId)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.inputList || []);
        if (!cancelled) setInputsByInstance(prev => ({ ...prev, [targetId]: list }));
      })
      .catch(() => { if (!cancelled) setInputsByInstance(prev => ({ ...prev, [targetId]: [] })); })
      .finally(() => { if (!cancelled) setLoadingInputs(false); });
    return () => { cancelled = true; };
  }, [targetId, showId, scriptFn, inputsByInstance]);

  const inputs = targetId ? (inputsByInstance[targetId] || []) : vmixInputs;
  const waitingForInputs = targetId && loadingInputs && !inputsByInstance[targetId];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* 1. Target vMix. "Primary" (empty) is the show's own vMix; named
             instances come from Workspace Settings. Always shown. */}
      <select
        value={targetId}
        onChange={e => setDraft(d => ({ ...d, target_instance_id: e.target.value }))}
        title="Target vMix instance"
        className={inputCls}
      >
        <option value="">Primary (show default)</option>
        {instances.map(inst => (
          <option key={inst.id} value={inst.id}>{inst.label} ({inst.host}:{inst.port})</option>
        ))}
      </select>

      {/* 2. Function */}
      <FunctionCombobox
        value={draft.function || ''}
        onChange={(v) => setDraft(d => ({
          ...d,
          function: v,
          // A script is addressed by name, never by input. Drop whatever was
          // typed for the previous function rather than hiding it, still set,
          // behind a control that is about to disappear.
          ...(isScriptFunction(v) ? { input: '' } : {}),
        }))}
      />

      {/* 3. Input — populated from the selected instance's vMix. Script
             functions take a name in Value and ignore Input entirely. */}
      {scriptFn ? null : waitingForInputs ? (
        <span className="text-xs text-gray-500 italic px-1">Loading inputs…</span>
      ) : inputs.length > 0 ? (
        <select
          value={draft.input || ''}
          onChange={e => setDraft(d => ({ ...d, input: e.target.value }))}
          className={inputCls}
        >
          <option value="">No input</option>
          {inputs.map(inp => (
            <option key={inp.number} value={String(inp.number)}>{inp.number}: {inp.name || inp.title}</option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={draft.input || ''}
          onChange={e => setDraft(d => ({ ...d, input: e.target.value }))}
          placeholder="Input #"
          title={targetId ? "This instance's inputs couldn't be loaded — enter an input number" : undefined}
          className={`w-16 ${inputCls}`}
        />
      )}

      {/* 4. Value — a script picker for ScriptStart/ScriptStop, free text otherwise */}
      {scriptFn ? (
        <ScriptValueField
          value={draft.value || ''}
          onChange={v => setDraft(d => ({ ...d, value: v }))}
          inputCls={inputCls}
        />
      ) : (
        <input
          type="text"
          value={draft.value || ''}
          onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
          placeholder="Value (opt)"
          className={`w-24 ${inputCls}`}
        />
      )}
    </div>
  );
}
